import { createSlice } from '@reduxjs/toolkit';
import {
  updateUnreadMsgs,
  realtimeMsg,
  sendMessage,
  updateUnreadThreads,
  getSequences,
  getConversation,
  getThreads,
  getThreadsWithUnreadMessages,
  enableAri,
  disableAri,
  updateArchivedThread,
  resetHasDuplicates,
  inboxFilterType,
} from '../actions';

import {
  hrsDay,
  millisecondsHr,
  messageTypes,
  channelTypes,
  inboxTypeKeys,
  sentVia,
  messageStatusType,
  candidateStatusTypes,
  inboxAccountName,
  TWO_DAYS_IN_HOURS,
  threadsPerPage,
} from 'appConstants';

const { TEAM, USER } = inboxTypeKeys;

const inboxPagination = {
  page: 1,
  totalItems: 0,
  totalPages: 0,
  itemsPerPage: 0,
};

const initialState = {
  inboxAccessListWithThreads: [],
  filterType: '',
  user: {
    ...inboxPagination,
    isLoading: true,
    threads: [],
    unreadThreads: [],
    unreadIncomingThreads: [],
    totalUnreadMessageCount: 0,
    accountName: '',
  },
  team: {
    ...inboxPagination,
    isLoading: true,
    threads: [],
    unreadThreads: [],
    unreadIncomingThreads: [],
    totalUnreadMessageCount: 0,
    accountName: '',
  },
  threadMessages: {
    hasMsgSendWindowExpired: true,
    inboxUnreadMessageCount: 0,
    teamInboxUnreadMessageCount: 0,
    isLoading: true,
    errorMessage: null,
    markedRead: false,
    // this is needed to whatsaaaaaaaaaap!
    wasActiveInLast24Hrs: true,
    messages: [],
    relatedThread: {
      messages: [],
    },
    ariState: {
      thread: {
        sequenceEnabled: false,
      },
      isEnabling: false,
      isDisabling: false,
      isLoading: false,
      sequences: [],
      unusedSequenceTypes: [],
    },
  },
};

const isStandardInbox = (accountName) =>
  accountName === inboxAccountName.user || accountName === inboxAccountName.team;

const getLoggedInUserIdentifier = (state) => {
  const myInboxEntry = state.inboxAccessListWithThreads?.find(
    (inbox) => inbox.accountName === inboxAccountName.user,
  );

  if (myInboxEntry?.userId) {
    return {
      userId: myInboxEntry.userId,
      userName: myInboxEntry.userName,
    };
  }

  if (state.user?.id) {
    return {
      userId: state.user.id,
      userName: state.user.userName,
    };
  }

  return {
    userId: null,
    userName: null,
  };
};

const isThreadOwnedByUser = (thread, userIdentifier) => {
  if (!thread || !userIdentifier) return false;

  if (userIdentifier.userId && thread.userId) {
    return thread.userId === userIdentifier.userId;
  }

  if (userIdentifier.userName && thread.userName) {
    return thread.userName === userIdentifier.userName;
  }

  return false;
};

const isInboxMatchingThread = (inboxData, thread) => {
  if (!inboxData || !thread) return false;

  if (inboxData.userId && thread.userId) {
    return inboxData.userId === thread.userId;
  }

  if (inboxData.userName && thread.userName) {
    return inboxData.userName === thread.userName;
  }

  return false;
};

const getTargetThreadIndexes = (state, threadId) => {
  const r = { user: -1, team: -1 };

  [TEAM, USER].forEach((inboxType) => {
    for (let i = 0; i < state[inboxType].threads.length; i++) {
      if (state[inboxType].threads[i].id === threadId) {
        r[inboxType] = i;
        return;
      }
    }
  });

  return r;
};

const addThread = ({ threads, thread, addThreadInUserInbox = true }) => {
  if (thread.userName === threads[0].userName && addThreadInUserInbox) {
    threads.unshift(thread);
  } else {
    // Find thread
    for (let i = 0; i < threads.length; i++) {
      if (threads[i].candidateId === thread.candidateId) {
        const l = threads.slice(0, i);
        const r = threads.slice(i + 1);

        threads = l.concat(thread).concat(r);
      }
    }
  }
  return threads;
};

// Find if the incoming thread exists
const getExistingThreadIndex = (threads, threadToUpdate) =>
  threads?.findIndex((thread) => thread.id === threadToUpdate.id);

const addNewThread = (state, thread, addThreadInUserInbox) => {
  if (addThreadInUserInbox && state.user.threads.length > 0) {
    state.user.threads = addThread({ threads: state.user.threads, thread, addThreadInUserInbox });
  }
  // User without license has no my inbox, add thread to team inbox
  const teamThreadIndex = getExistingThreadIndex(state.team.threads, thread);
  if (teamThreadIndex === -1) {
    state.team.threads = [thread, ...state.team.threads];
  }
};

const didCandidateWhatsAppInLast24hrs = (messages) => {
  let candidateRespondedInLast24Hrs = false;
  const now = new Date();

  for (const message of messages) {
    if (
      message.channel === channelTypes.WHATS_APP &&
      message.type === messageTypes.INBOUND &&
      (now - new Date(message.time)) / millisecondsHr < hrsDay
    ) {
      candidateRespondedInLast24Hrs = true;
      break;
    }
  }

  return candidateRespondedInLast24Hrs;
};

const handleSentMessages = (messageOptions, threads) => {
  if (threads.length) {
    threads = threads.filter((thread) => {
      // If we are looking for sent messages only, filter out any threads that don't have lastMessageSent as the API will still return these
      if (messageOptions !== messageStatusType.SENT) {
        return true;
      }

      return thread.lastMessageSent;
    });
  }
  return threads;
};

const hasWeChatMsgWindowExpired = (state, messages) => {
  let isMessagingDisabled = true;
  const now = new Date();

  for (const message of messages) {
    if (
      message.type === messageTypes.INBOUND &&
      message.channel === channelTypes.WECHAT &&
      (now - new Date(message.time)) / millisecondsHr < TWO_DAYS_IN_HOURS
    ) {
      state.threadMessages.hasMsgSendWindowExpired = false;
      isMessagingDisabled = false;
      break;
    }
  }

  return isMessagingDisabled;
};

const aggregatePaginatedThreads = (paginatedThreads, page) => {
  let res = [];
  for (let p = 1; p <= page; p++) {
    const pageThreads = paginatedThreads?.[p];
    if (pageThreads) {
      res = res.concat(pageThreads);
    }
  }
  return res;
};

const updateStandardInbox = (state, inboxType, page, threads) => {
  if (!state[inboxType].paginatedThreads) {
    state[inboxType].paginatedThreads = {};
  }
  state[inboxType].paginatedThreads[page] = threads;
  const aggregatedThreads = aggregatePaginatedThreads(state[inboxType].paginatedThreads, page);

  if (state.filterType === 'unread') {
    state[inboxType].unreadThreads = aggregatedThreads;
  }
  return aggregatedThreads;
};

const updateCustomInbox = (state, inboxIdx, page, threads) => {
  const inboxData = state.inboxAccessListWithThreads[inboxIdx];
  if (!inboxData) return [];

  if (!inboxData.paginatedThreads) {
    inboxData.paginatedThreads = {};
  }
  inboxData.paginatedThreads[page] = threads;
  const aggregatedThreads = aggregatePaginatedThreads(inboxData.paginatedThreads, page);

  if (state.filterType === 'unread') {
    inboxData.unreadThreads = aggregatedThreads;
  }
  return aggregatedThreads;
};

const updateInboxThreads = (
  state,
  { inboxType, page, threads, messageOptions, accountName, inboxIdx },
) => {
  threads = handleSentMessages(messageOptions, threads);

  if (threads.length) {
    if (isStandardInbox(accountName)) {
      return updateStandardInbox(state, inboxType, page, threads);
    }
    return updateCustomInbox(state, inboxIdx, page, threads);
  } else if (page > 1) {
    return isStandardInbox(accountName)
      ? state[inboxType].threads
      : state.inboxAccessListWithThreads[inboxIdx]?.threads;
  }

  return [];
};

const handleWhatsAppMsgActivity = (state, message) => {
  const didWhatsAppInLast24Hrs =
    message.channel === channelTypes.WHATS_APP &&
    message.type === messageTypes.INBOUND &&
    (new Date() - new Date(message.time)) / millisecondsHr < hrsDay;

  if (didWhatsAppInLast24Hrs) {
    state.threadMessages.wasActiveInLast24Hrs = true;
  }
};

const formatSingleMessage = (message) => {
  const isOutBoundMessage = message.type === messageTypes.OUTBOUND;

  if (message.type === messageTypes.INBOUND && !message.candidateName) {
    message.candidateName = '';
  }

  if (isOutBoundMessage && !message.userFirstName) {
    message.userFirstName = '';
  }

  if (isOutBoundMessage && !message.sentVia && message.messageText) {
    // TODO: This is a temp. solution to unblock the broken UI. Should resolve with more exacting data -Amir
    // See DGA-241
    message.sentVia = sentVia.HUMAN;
  }
};

/*
 * To ensure the thread's message status is updated regardless of the WebSocket event or API response,
 * we check the status of the message and update it if the message has failed.
 */
const checkIfMessageExistsAndRemoveIfNewMessageIsFailed = (newMessage, messages) => {
  const existingMessageIndex = messages.findIndex((msg) => msg.id === newMessage.id);
  if (existingMessageIndex === -1) return false;
  const existingMessage = messages[existingMessageIndex];
  const isStatusDowngrade = newMessage.status === 'failed' && existingMessage.status !== 'failed';
  if (isStatusDowngrade) {
    messages.splice(existingMessageIndex, 1);
    return false;
  }

  return true;
};

const updateRelatedThread = (state, relatedThread, thread, filteredMsgs) => {
  const currentThreadId =
    relatedThread.id || relatedThread.threadId || state.threadMessages.messages[0]?.threadId;

  if (currentThreadId && thread.id === currentThreadId) {
    state.threadMessages.relatedThread = { ...relatedThread, ...thread, messages: filteredMsgs };
  }
};

const updateInboxThreadsWithMessage = (state, thread, message, indexes) => {
  const inboxes = [];

  const userIdentifier = getLoggedInUserIdentifier(state);

  const threadBelongsToUser = isThreadOwnedByUser(thread, userIdentifier);

  if (indexes.user !== -1 && threadBelongsToUser) {
    inboxes.push(USER);
  }

  if (indexes.team !== -1) {
    inboxes.push(TEAM);
  }

  inboxes.forEach((inbox) => {
    const left = state[inbox].threads.slice(0, indexes[inbox]);
    const right = state[inbox].threads.slice(indexes[inbox] + 1);
    const isInbound = message.type === messageTypes.INBOUND;
    const unreadChange = isInbound ? 1 : -state[inbox].threads[indexes[inbox]].unreadMessageCount;
    state[inbox].totalUnreadMessageCount = Math.max(
      0,
      state[inbox].totalUnreadMessageCount + unreadChange,
    );
    state[inbox].threads = [thread, ...left, ...right];
  });

  if (inboxes.includes(USER)) {
    state.threadMessages.inboxUnreadMessageCount = state.user.totalUnreadMessageCount;
  }
  if (inboxes.includes(TEAM)) {
    state.threadMessages.teamInboxUnreadMessageCount = state.team.totalUnreadMessageCount;
  }

  return inboxes;
};

const handleNoInboxThreads = (state, thread, message) => {
  addNewThread(state, thread, false);
  if (message.type === messageTypes.INBOUND) {
    state.team.totalUnreadMessageCount += 1;
    const exists = state.team.unreadThreads.find((t) => t.id === thread.id);
    if (!exists) {
      state.team.unreadThreads = [thread, ...state.team.unreadThreads];
    }
    state.threadMessages.teamInboxUnreadMessageCount = state.team.totalUnreadMessageCount;
  }
};

const updateCustomInboxWithRealtimeMessage = (state, thread, message) => {
  let updated = false;

  state.inboxAccessListWithThreads.forEach((inboxData, index) => {
    if (isStandardInbox(inboxData.accountName)) return;
    if (!isInboxMatchingThread(inboxData, thread)) return;

    if (message.type === messageTypes.INBOUND) {
      inboxData.totalUnreadMessageCount = inboxData?.unreadThreads?.length;
      state.inboxAccessListWithThreads[index] = { ...inboxData };
      updated = true;
    }

    if (inboxData.threads?.length) {
      const threadIndex = inboxData.threads.findIndex((t) => t.id === thread.id);
      if (threadIndex !== -1) {
        const left = inboxData.threads.slice(0, threadIndex);
        const right = inboxData.threads.slice(threadIndex + 1);
        const updatedThread = {
          ...thread,
          messageText: message.messageText,
          time: message.time,
          type: message.type,
          channel: message.channel,
          lastMessageSent:
            message.type === messageTypes.OUTBOUND ? message : thread.lastMessageSent,
        };
        inboxData.threads = [updatedThread, ...left, ...right];
        state.inboxAccessListWithThreads[index] = { ...inboxData };
        updated = true;
      }
    }
  });

  return updated;
};

const setMessages = (state, action) => {
  const { message, thread } = action.payload;
  const { messages, relatedThread } = state.threadMessages;

  if (checkIfMessageExistsAndRemoveIfNewMessageIsFailed(message, messages)) return;

  formatSingleMessage(message);
  handleWhatsAppMsgActivity(state, message);
  hasWeChatMsgWindowExpired(state, messages);

  messages.push(message);

  const filteredMsgs = messages.filter((msg) => msg.threadId === thread.id);
  updateRelatedThread(state, relatedThread, thread, filteredMsgs);

  const indexes = getTargetThreadIndexes(state, thread.id);
  const inboxes = updateInboxThreadsWithMessage(state, thread, message, indexes);

  const customInboxUpdated = updateCustomInboxWithRealtimeMessage(state, thread, message);

  if (!inboxes.length && !customInboxUpdated) {
    handleNoInboxThreads(state, thread, message);
  }
};

const formatMessages = (messages) =>
  messages.map((msg) => {
    formatSingleMessage(msg);

    return msg;
  });

const updateUnreadMessages = (state, action) => {
  const targetThread = { ...action.payload };
  const indexes = getTargetThreadIndexes(state, targetThread.id);

  targetThread.unreadMessageCount = 0;
  targetThread.read = true;

  const updatedCount = (currentCount, unreadMessageCount = 0) => {
    const count = currentCount - unreadMessageCount;

    return count < 0 ? 0 : count;
  };

  const updateTeamThreads = () => {
    const teamLeftThreads = state.team.threads.slice(0, indexes.team);
    const teamRightThreads = state.team.threads.slice(indexes.team + 1);

    state.team.threads = teamLeftThreads.concat(targetThread).concat(teamRightThreads);
    state.team.totalUnreadMessageCount = updatedCount(
      state.team.totalUnreadMessageCount,
      action.payload.unreadMessageCount,
    );
  };

  // Found in both inboxes (threads)
  if (indexes.user !== -1) {
    const userLeftThreads = state.user.threads.slice(0, indexes.user);
    const userRightThreads = state.user.threads.slice(indexes.user + 1);

    updateTeamThreads();
    state.user.threads = userLeftThreads.concat(targetThread).concat(userRightThreads);
    state.user.totalUnreadMessageCount = updatedCount(
      state.user.totalUnreadMessageCount,
      action.payload.unreadMessageCount,
    );
  }
  // TODO: this is temp workaround for TXTE-26589 need to handle this properly
  if (state.filterType === 'unread') {
    const existingThreadIndex = getExistingThreadIndex(state.user.unreadThreads, targetThread);

    if (existingThreadIndex !== -1) {
      // Update the existing thread
      state.user.unreadThreads[existingThreadIndex] = targetThread;
    } else {
      // Add the new thread
      const userLeftUnreadThreads = state.user.unreadThreads.slice(0, indexes.user);
      const userRightUnreadThreads = state.user.unreadThreads.slice(indexes.user + 1);

      state.user.unreadThreads = userLeftUnreadThreads
        .concat(targetThread)
        .concat(userRightUnreadThreads);
    }
    state.user.totalUnreadMessageCount = updatedCount(
      state.user.totalUnreadMessageCount,
      action.payload.unreadMessageCount,
    );
  }

  // Team only
  if (indexes.team !== -1) {
    updateTeamThreads();
  }

  state.inboxAccessListWithThreads.forEach((inboxData, index) => {
    if (!isInboxMatchingThread(inboxData, targetThread)) return;

    if (action.payload.unreadMessageCount > 0) {
      inboxData.totalUnreadMessageCount = updatedCount(
        inboxData.totalUnreadMessageCount,
        action.payload.unreadMessageCount,
      );
    }

    if (inboxData.threads && inboxData.threads.length) {
      const threadIndex = inboxData.threads.findIndex((t) => t.id === targetThread.id);
      if (threadIndex !== -1) {
        const left = inboxData.threads.slice(0, threadIndex);
        const right = inboxData.threads.slice(threadIndex + 1);
        inboxData.threads = left.concat(targetThread).concat(right);
      }
    }

    state.inboxAccessListWithThreads[index] = { ...inboxData };
  });
};
const handleUnreadThreads = (state, action) => {
  if (action.payload.thread) {
    const { thread: threadToUpdate } = action.payload;
    const { unreadThreads: userUnreadThreads, unreadIncomingThreads: userUnreadIncomingThreads } =
      state.user;
    const { unreadThreads: teamUnreadThreads, unreadIncomingThreads: teamUnreadIncomingThreads } =
      state.team;

    const updateThreadState = (unreadThreads, existingThreadIndex) => {
      if (threadToUpdate.read) {
        // If the incoming thread exists and is `read`, remove the incoming thread from `unreadThreads`.
        if (existingThreadIndex !== -1 && unreadThreads) {
          const leftThreads = unreadThreads.slice(0, existingThreadIndex);
          const rightThreads = unreadThreads.slice(existingThreadIndex + 1);

          unreadThreads = leftThreads.concat(rightThreads);
        }
      } else if (existingThreadIndex === -1) {
        // If the incoming thread doesnt exists in unreadThreads, insert the incoming thread to `unreadThreads`.
        unreadThreads.unshift(threadToUpdate);
      } else if (unreadThreads) {
        // If the incoming thread exists in unreadThreads, update the existing thread.
        unreadThreads[existingThreadIndex] = threadToUpdate;
      }
      return unreadThreads;
    };

    const userIdentifier = getLoggedInUserIdentifier(state);

    const threadBelongsToUser = isThreadOwnedByUser(threadToUpdate, userIdentifier);

    const existingUserThreadIndex = getExistingThreadIndex(userUnreadThreads, threadToUpdate);
    const existingTeamThreadIndex = getExistingThreadIndex(teamUnreadThreads, threadToUpdate);
    const existingUserIncomingThreadIndex = getExistingThreadIndex(
      userUnreadIncomingThreads,
      threadToUpdate,
    );
    const existingTeamIncomingThreadIndex = getExistingThreadIndex(
      teamUnreadIncomingThreads,
      threadToUpdate,
    );

    if (threadBelongsToUser || existingUserThreadIndex !== -1) {
      state.user.unreadThreads = updateThreadState(userUnreadThreads, existingUserThreadIndex);
      state.user.unreadIncomingThreads = updateThreadState(
        userUnreadIncomingThreads,
        existingUserIncomingThreadIndex,
      );
    }

    state.team.unreadThreads = updateThreadState(teamUnreadThreads, existingTeamThreadIndex);
    state.team.unreadIncomingThreads = updateThreadState(
      teamUnreadIncomingThreads,
      existingTeamIncomingThreadIndex,
    );

    if (state.inboxAccessListWithThreads?.length > 0) {
      state.inboxAccessListWithThreads.forEach((inboxData, index) => {
        if (!isInboxMatchingThread(inboxData, threadToUpdate)) return;

        if (threadToUpdate.read) {
          inboxData.totalUnreadMessageCount = Math.max(
            0,
            (inboxData.totalUnreadMessageCount || 0) - 1,
          );
        }

        if (inboxData.unreadThreads) {
          const existingIdx = getExistingThreadIndex(inboxData.unreadThreads, threadToUpdate);
          inboxData.unreadThreads = updateThreadState([...inboxData.unreadThreads], existingIdx);
        }

        state.inboxAccessListWithThreads[index] = { ...inboxData };
      });
    }
  }
};
const handleArchivedThreads = (state, action) => {
  const { threadId, isArchived } = action.payload;
  const inboxes = [];

  const indexes = getTargetThreadIndexes(state, threadId);

  if (indexes.team !== -1) {
    inboxes.push(TEAM);
  }

  if (indexes.user !== -1) {
    inboxes.push(USER);
  }

  inboxes.forEach((inbox) => {
    const { threads } = state[inbox];
    const index = indexes[inbox];

    const leftThreads = threads.slice(0, index);
    const rightThreads = threads.slice(index + 1);
    const currentThread = threads[index];

    const status = isArchived ? candidateStatusTypes.DELETED : candidateStatusTypes.ACTIVE;
    const updatedThread = {
      ...currentThread,
      belongsToDeletedCandidate: isArchived,
      status,
    };

    state[inbox].threads = leftThreads.concat(updatedThread).concat(rightThreads);
  });
};

// create team/user inbox pagination
const updateInboxPagination = ({ state, inboxType, paging, itemsPerPage, threads }) => {
  state[inboxType].page = paging.page;
  state[inboxType].totalItems = paging.items || 0;
  state[inboxType].totalPages = paging.pages;
  state[inboxType].itemsPerPage = itemsPerPage || threads.length;
};

const filterDuplicateThreads = (threads) => {
  const finalThreads = new Map();
  threads.forEach((thread) => {
    if (
      !(
        finalThreads.has(thread.candidateId) &&
        finalThreads.get(thread.candidateId).candidateId === thread.candidateId
      )
    ) {
      finalThreads.set(thread.candidateId, thread);
    }
  });
  return Array.from(finalThreads.values());
};

const updateStateWithThreads = (state, { inboxType, action, threads, filteredThreads }) => {
  const { accountName, inboxIdx } = action.meta.arg;

  if (isStandardInbox(accountName)) {
    state[inboxType].threads = filteredThreads;
    state[inboxType].hasDuplicates = filteredThreads.length < threads.length;
    state[inboxType].page = action.payload.paging.page;
    state[inboxType].threads = handleSentMessages(action.meta.arg.messageOptions, filteredThreads);
    updateInboxPagination({
      state,
      inboxType,
      paging: action.payload.paging,
      itemsPerPage: action.meta.arg.itemsPerPage,
      threads,
    });
    state[inboxType].userName = action.meta.arg.userName;
    state[inboxType].inboxIdx = action.meta.arg.inboxIdx;
    state[inboxType].accountName = accountName;
    state[inboxType].endOfThreads = threads.length < threadsPerPage;
  } else {
    const inboxData = state.inboxAccessListWithThreads[inboxIdx];
    if (inboxData) {
      inboxData.threads = filteredThreads;
      inboxData.hasDuplicates = filteredThreads.length < threads.length;
      inboxData.page = action.payload.paging.page;
      inboxData.threads = handleSentMessages(action.meta.arg.messageOptions, filteredThreads);
      inboxData.totalItems = action.payload.paging.items;
      inboxData.totalPages = action.payload.paging.pages;
      inboxData.itemsPerPage = action.meta.arg.itemsPerPage || threadsPerPage;
      inboxData.endOfThreads = threads.length < threadsPerPage;
      inboxData.isLoading = false;
    }
  }
};
const threadSlice = createSlice({
  name: 'thread',
  initialState,
  reducers: {
    // Add reducer for selecting thread from dashboard
    selectThreadFromDashboard: (state, action) => {
      const thread = action.payload;
      state.user.threads = [thread, ...state.user.threads];
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getThreads.fulfilled, (state, action) => {
      const inboxType = action.meta.arg.inboxType.toLowerCase();
      const { messageOptions, accountName, inboxIdx } = action.meta.arg;
      const threads = action.payload.threads || [];

      if (isStandardInbox(accountName)) {
        state[inboxType].isLoading = false;
      } else {
        const inboxData = state.inboxAccessListWithThreads[inboxIdx];
        if (inboxData) {
          inboxData.isLoading = false;
        }
      }
      if (threads.length || action.payload.paging.page === 1) {
        const updatedThreads = updateInboxThreads(state, {
          inboxType,
          page: action.payload.paging.page,
          threads,
          messageOptions,
          accountName,
          inboxIdx,
        });
        const filteredThreads = filterDuplicateThreads(updatedThreads);

        updateStateWithThreads(state, { inboxType, action, threads, filteredThreads });
      } else {
        state[inboxType].endOfThreads = true;
      }
    });

    builder.addCase(getThreads.pending, (state, action) => {
      const inboxType = action.meta.arg.inboxType.toLowerCase();
      const { accountName, inboxIdx } = action.meta.arg;

      if (isStandardInbox(accountName)) {
        state[inboxType].isLoading = true;
      } else {
        const inboxData = state.inboxAccessListWithThreads[inboxIdx];
        if (inboxData) {
          inboxData.isLoading = true;
        }
      }
    });

    builder.addCase(getThreads.rejected, (state, action) => {
      const inboxType = action.meta.arg.inboxType.toLowerCase();
      state[inboxType].error = action.payload;
      state[inboxType].isLoading = false;
    });

    builder.addCase(getConversation.pending, (state) => {
      state.threadMessages.isLoading = true;
    });

    builder.addCase(getConversation.fulfilled, (state, action) => {
      const { messages, thread, relatedThread } = action.payload;
      state.threadMessages.isLoading = false;
      // .reverse() is a temp. workaround until textrecruit-common/MessageThread is tested and sorts as expected -Amir;
      state.threadMessages.messages = formatMessages(messages);
      state.threadMessages.wasActiveInLast24Hrs = didCandidateWhatsAppInLast24hrs(messages);
      state.threadMessages.relatedThread = { ...thread, ...relatedThread } || {};
      state.threadMessages.relatedThread.messages = formatMessages(messages);
      state.threadMessages.ariState.thread.sequenceEnabled = Boolean(
        thread && thread.sequenceEnabled,
      );
      state.threadMessages.hasMsgSendWindowExpired = hasWeChatMsgWindowExpired(state, messages);
    });

    builder.addCase(getConversation.rejected, (state, action) => {
      state.threadMessages.isLoading = false;
      state.threadMessages.error = action.payload.error;
    });

    builder.addCase(inboxFilterType, (state, action) => {
      state.filterType = action.payload;
    });
    builder.addCase(sendMessage.fulfilled, (state, action) => setMessages(state, action));
    builder.addCase(realtimeMsg, (state, action) => setMessages(state, action));
    builder.addCase(updateUnreadThreads, (state, action) => handleUnreadThreads(state, action));
    builder.addCase(updateUnreadMsgs, (state, action) => updateUnreadMessages(state, action));

    builder.addCase(getThreadsWithUnreadMessages.pending, (state, action) => {
      const inboxType = action.meta.arg.inboxType.toLowerCase();
      const { accountName, inboxIdx } = action.meta.arg;

      if (isStandardInbox(accountName)) {
        state[inboxType].isLoading = true;
      } else {
        const inboxData = state.inboxAccessListWithThreads[inboxIdx];
        if (inboxData) {
          inboxData.isLoading = true;
        }
      }
    });

    builder.addCase(getThreadsWithUnreadMessages.fulfilled, (state, action) => {
      const inboxType = action.meta.arg.inboxType.toLowerCase();
      const itemsPerPage = action.meta.arg.itemsPerPage || 0;
      const { threads, paging } = action.payload;
      const { accountName, inboxIdx } = action.meta.arg;

      if (accountName === inboxAccountName.user) {
        state.threadMessages.inboxUnreadMessageCount = paging.items;
      }
      if (accountName === inboxAccountName.team) {
        state.threadMessages.teamInboxUnreadMessageCount = paging.items;
      }

      if (isStandardInbox(accountName)) {
        state[inboxType].unreadThreads = threads;
        state[inboxType].accountName = accountName;
        state[inboxType].totalUnreadMessageCount = paging.items;
        updateInboxPagination({
          state,
          inboxType,
          paging,
          itemsPerPage,
          threads,
        });
      } else {
        const inboxData = state.inboxAccessListWithThreads[inboxIdx];
        if (inboxData) {
          inboxData.unreadThreads = threads;
          inboxData.totalUnreadMessageCount = paging.items;
          inboxData.totalItems = paging.items;
          inboxData.totalPages = paging.pages;
          inboxData.itemsPerPage = itemsPerPage;
          inboxData.isLoading = false;
        }
      }

      // Below logic need to be changed based on API update
      let inbx = {};
      action.meta.arg.inboxAccessList.forEach((inbox) => {
        if (inbox.accountName === action.meta.arg.accountName) {
          inbx.totalUnreadMessageCount = paging.items || 0;
          inbx.unreadThreads = threads.length ? threads : [];
          inbx = { ...inbox, ...inbx };
        }
      });

      const matchedIndex = state.inboxAccessListWithThreads.findIndex(
        (l) => l.accountName === inbx.accountName && l.accountType === inbx.accountType,
      );

      if (matchedIndex < 0) {
        state.inboxAccessListWithThreads.push(inbx);
      } else {
        state.inboxAccessListWithThreads[matchedIndex].unreadThreads = threads;
        state.inboxAccessListWithThreads[matchedIndex].totalUnreadMessageCount = paging.items;
      }

      state[inboxType].isLoading = false;
    });

    builder.addCase(getThreadsWithUnreadMessages.rejected, (state, action) => {
      const inboxType = action.meta.arg.inboxType.toLowerCase();
      state[inboxType].isLoading = false;
    });
    builder.addCase(updateArchivedThread, (state, action) => handleArchivedThreads(state, action));

    builder.addCase(getSequences.pending, (state) => {
      state.threadMessages.ariState.isLoading = true;
    });

    builder.addCase(getSequences.fulfilled, (state, action) => {
      const { sequences = [], unusedSequenceTypes = [] } = action.payload;

      state.threadMessages.ariState.isLoading = false;
      state.threadMessages.ariState.sequences = sequences;
      state.threadMessages.ariState.unusedSequenceTypes = unusedSequenceTypes;
    });

    builder.addCase(getSequences.rejected, (state) => {
      state.threadMessages.ariState.isLoading = false;
      state.threadMessages.ariState.error = 'Error';
    });

    builder.addCase(enableAri.pending, (state) => {
      state.threadMessages.ariState.isEnabling = true;
    });

    builder.addCase(enableAri.fulfilled, (state, action) => {
      state.threadMessages.ariState.isEnabling = false;
      state.threadMessages.ariState.thread = action.payload.thread;
    });

    builder.addCase(enableAri.rejected, (state) => {
      state.threadMessages.ariState.isEnabling = false;
      state.threadMessages.ariState.error = 'Error';
    });

    builder.addCase(disableAri.pending, (state) => {
      state.threadMessages.ariState.isDisabling = true;
    });

    builder.addCase(disableAri.fulfilled, (state, action) => {
      state.threadMessages.ariState.isDisabling = false;
      state.threadMessages.ariState.thread = action.payload.thread;
    });

    builder.addCase(disableAri.rejected, (state) => {
      state.threadMessages.ariState.isDisabling = false;
      state.threadMessages.ariState.error = 'Error';
    });

    builder.addCase(resetHasDuplicates, (state, action) => {
      const { inboxType } = action.payload;
      state[inboxType].hasDuplicates = false;
    });
  },
});

export default threadSlice;
